/* =========================================================================
   VRO personalization engine

   One place that knows three things, so no section has to know any of them:

     1. WHO the shopper is        -> VroPersonalization.profile
     2. WHAT they told us         -> the quiz answers + the in-section facets
     3. WHICH products honour it  -> strict, structured matching

   Design notes that matter, because they are the whole point of this file:

   * The candidate pool is a COLLECTION, never /search. Shopify's storefront
     search ranks by text, so "hair growth" happily returns a perfume whose
     copy says "growth". A collection is a shelf the merchant curated, so the
     same answers always look at the same products — deterministic by
     construction.

   * Relevance is decided against the store's own STRUCTURED fields, stamped
     on each card by snippets/feed-product-card.liquid: VRO Needs, Hair
     Concern, Skin Concern, Health Concerns, Key Benefits, the grooming /
     nutrition namespaces and the product's tags. Never the title, never the
     description. A shirt carries no hair concern and no hair benefit, so a
     shirt cannot reach a hair result no matter what its copy says.

   * The budget is a HARD range. If nothing in the shelf matches inside it we
     say so; we do not quietly widen it and hand back things the shopper
     already told us they will not buy.

   * Nothing here touches the theme's existing scripts. It adds a global and
     some data-attributes; every existing handler (quick add, wishlist,
     size quiz, tabs) keeps working on the cards it renders because they are
     the same cards, from the same snippet, bound by delegation on document.
   ========================================================================= */
(function () {
  'use strict';

  if (window.VroPersonalization) return;

  var CFG = window.VroPersonalizationConfig || {};
  var STORAGE_PREFIX = 'vro_personalization_v1';
  var POOL_MAX_PAGES = CFG.maxPages || 4;   /* 50 products a page */
  var DEFAULT_SHOW = CFG.showCount || 12;
  /* How many picks the phone's grid shows before the View All tile closes
     it. custom.css hides the rest at the same number; the two have to say
     the same thing or the tile stops meaning "there are more". */
  var GRID_SHOW = 5;

  /* ---------------------------------------------------------------
     Text helpers. Everything is compared lowercase, with punctuation
     flattened to single spaces, so "Anti-Dandruff", "anti dandruff" and
     "Anti  Dandruff" are one thing.

     The one character kept is "~", standing in for every separator between
     two values — the pipes Liquid joins the fields with, and the line
     breaks inside a bulleted Key Benefits. Without a boundary marker,
     "…nourishes hair" followed by "growth serum" would read as one string
     containing "hair growth" and quietly match a product that says no such
     thing. No phrase we search for contains "~", so none can span two
     fields.
  --------------------------------------------------------------- */
  function norm(value) {
    return String(value == null ? '' : value)
      .toLowerCase()
      .replace(/&amp;/g, '&')
      .replace(/[|\n\r]+/g, ' ~ ')
      .replace(/[^a-z0-9~]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function includesAny(haystack, needles) {
    for (var i = 0; i < needles.length; i++) {
      if (haystack.indexOf(needles[i]) > -1) return true;
    }
    return false;
  }

  /* ---------------------------------------------------------------
     The shopper's profile.

     Guests keep it in localStorage. A logged-in shopper keeps it under
     their own customer id, so two people on one laptop never see each
     other's picks, and a guest who then logs in carries their answers
     across once.

     This store has two ways of being "logged in": a real Shopify customer
     session (window.__shopifyCustomerId, set in layout/theme.liquid) and
     the OTP shadow session that assets/login-sheet.js keeps under
     `vro_session` — the same customer id either way, so both are honoured.

     Server persistence is deliberately opt-in and reuses the auth that
     already exists: the OTP backend's base URL from theme settings plus
     that session's bearer token. No endpoint is invented here and nothing
     is written anywhere unless the merchant has configured a path, so the
     default behaviour adds no new surface at all.
  --------------------------------------------------------------- */
  var profile = (function () {
    var memory = null;      /* last known profile, so a blocked localStorage still works in-session */
    var memoryOwner = null;

    function shopifyCustomerId() {
      var id = window.__shopifyCustomerId;
      return (id === null || id === undefined || id === 'null') ? null : String(id);
    }

    function sessionCustomerId() {
      try {
        if (!window.VroSession || !window.VroSession.load) return null;
        var s = window.VroSession.load();
        if (s && s.customer && s.customer.id) return String(s.customer.id);
      } catch (e) { /* session unreadable, treat as guest */ }
      return null;
    }

    function owner() {
      return shopifyCustomerId() || sessionCustomerId() || null;
    }

    function keyFor(id) {
      return STORAGE_PREFIX + ':' + (id ? 'c' + id : 'guest');
    }

    function readKey(key) {
      try {
        var raw = localStorage.getItem(key);
        if (!raw) return null;
        var parsed = JSON.parse(raw);
        return (parsed && typeof parsed === 'object') ? parsed : null;
      } catch (e) { return null; }
    }

    function writeKey(key, value) {
      try { localStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* private mode, ignore */ }
    }

    function blank() {
      return { v: 1, quiz: null, facets: {}, facetOrder: [], updatedAt: 0 };
    }

    /* ----------------------------------------------------------------
       Fifteen memories, and not one more.

       A memory is not a facet. Four rails on one page each ask what your
       skin does, and each stores its own answer under its own section id
       — four facets, one card on the profile, because the profile keys
       cards by what they say rather than by who asked. One facet can also
       be worth two: the size memory carries an alpha size and a waist
       measurement and shows them separately. So counting keys would count
       the wrong thing in both directions, and capping keys would quietly
       throw away a rail's answer while the shopper still saw five cards.

       These are the same ids assets/profile-memories.js renders under, and
       they are exported below so that file can use them rather than keep a
       second copy of the rule. Change one, change both.
    ---------------------------------------------------------------- */
    var MEMORY_CAP = 15;

    /* Keys are storage, not memories, and a key whose shape no longer
       renders anything shows up in neither count. This stops those
       accumulating without bound. Generous: at four rails to a memory it
       is still well clear of the cap above. */
    var FACET_CEILING = 90;

    function slug(value) {
      return String(value).toLowerCase().replace(/\W+/g, '-');
    }

    function memoryIdsFor(name, value) {
      var base = String(name).split('@')[0];
      if (base === 'size_memory') {
        if (!value || typeof value !== 'object') return [];
        var ids = [];
        if (value.alpha && value.alpha.value) ids.push('vro-size-alpha');
        if (value.numeric && value.numeric.value) ids.push('vro-size-numeric');
        return ids;
      }
      /* A quiz answer recorded by assets/vro-quiz-memory.js: one memory per
         quiz, keyed by the quiz, so a second answer replaces the first. */
      if (base === 'answer') {
        if (!value || typeof value !== 'object' || !value.a) return [];
        return ['vro-answer-' + slug(String(name).split('@')[1] || name)];
      }
      if (typeof value !== 'string' || !value.trim()) return [];
      if (base === 'skin') return ['vro-skin-' + slug(value)];
      if (base === 'hair') return ['vro-hair-' + slug(value)];
      if (base === 'goal') return ['vro-goal-' + slug(value)];
      if (base === 'body') return ['vro-body'];
      if (base === 'size') return ['vro-size-' + slug(value)];
      return [];
    }

    /* id -> how many facets are currently producing it. The quiz counts as
       one memory of its own and is never pruned: it is the answer the
       shopper gave deliberately, and removing it is the one thing the
       profile page already offers them. */
    function memoryTally(profile) {
      var tally = {};
      function add(id) { tally[id] = (tally[id] || 0) + 1; }
      if (profile.quiz && profile.quiz.category) add('vro-quiz');
      var facets = profile.facets || {};
      Object.keys(facets).forEach(function (name) {
        memoryIdsFor(name, facets[name]).forEach(add);
      });
      return tally;
    }

    /* Oldest first. The stored order is the record of what was answered
       when; anything in it that is no longer a facet is dropped, and any
       facet it never heard of is appended — which is what migrates a
       profile written before this existed, and what absorbs a facet
       written by anything that sets one without going through setFacet. */
    function orderedNames(profile) {
      var facets = profile.facets || {};
      var seen = {};
      var order = (profile.facetOrder || []).filter(function (name) {
        if (seen[name] || !Object.prototype.hasOwnProperty.call(facets, name)) return false;
        seen[name] = true;
        return true;
      });
      Object.keys(facets).forEach(function (name) { if (!seen[name]) order.push(name); });
      return order;
    }

    /* The oldest facet whose removal would actually cost a card — one
       carrying an id no other facet is also producing. A duplicate is
       skipped rather than deleted: it is free, and dropping it would lose a
       rail's answer without ever bringing the count down. If nothing in the
       list is sole, the count cannot be reduced and pruning stops. */
    function oldestThatCosts(profile, order) {
      var tally = memoryTally(profile);
      for (var i = 0; i < order.length; i++) {
        var ids = memoryIdsFor(order[i], profile.facets[order[i]]);
        for (var j = 0; j < ids.length; j++) {
          if (tally[ids[j]] === 1) return order[i];
        }
      }
      return null;
    }

    /* Runs on every write, so the stored profile is never over the cap
       rather than being trimmed when someone remembers to ask. All of it is
       plain object work over a few dozen keys, on a click the shopper just
       made — no storage reads, no network, nothing on the render path. */
    function prune(next) {
      next.facets = next.facets || {};
      var order = orderedNames(next);

      while (Object.keys(memoryTally(next)).length > MEMORY_CAP) {
        var victim = oldestThatCosts(next, order);
        if (!victim) break;
        delete next.facets[victim];
        order.splice(order.indexOf(victim), 1);
      }

      while (order.length > FACET_CEILING) {
        delete next.facets[order.shift()];
      }

      next.facetOrder = order;
      /* Timestamps follow their facets out. */
      if (next.facetAt) {
        Object.keys(next.facetAt).forEach(function (name) {
          if (!next.facets[name]) delete next.facetAt[name];
        });
      }
      return next;
    }

    function get() {
      var id = owner();
      if (memory && memoryOwner === id) return memory;

      var stored = readKey(keyFor(id));

      /* A guest who has just logged in keeps the answers they gave as a
         guest — once. After that the two records are separate. */
      if (!stored && id) {
        var guest = readKey(keyFor(null));
        if (guest) {
          stored = guest;
          writeKey(keyFor(id), stored);
        }
      }

      memory = stored || blank();
      memoryOwner = id;
      return memory;
    }

    function save(next) {
      var id = owner();
      next.v = 1;
      /* The one gate every write passes through — setQuiz, setFacet, and the
         profile page removing a card by hand all end up here — so the cap is
         an invariant of what is stored rather than a tidy-up someone has to
         remember to call. */
      prune(next);
      next.updatedAt = Date.now();
      memory = next;
      memoryOwner = id;
      writeKey(keyFor(id), next);
      pushRemote(next, id);
      emit('vro:profile:change', { profile: next, customerId: id });
      return next;
    }

    /* How long an answer counts as just given. A card asked again after
       this is a fresh question to a returning shopper, which is the point:
       the answer itself stays on the profile and keeps shaping the rails,
       only the card stops treating it as settled. 24 hours unless the
       theme's config says otherwise. Size and body type are memories, not
       quizzes in this sense, and are never re-asked from here (see
       assets/vro-pdp-quiz.js and the size branch in vro-subfilter.js). */
    var REFRESH_MS = (Number(CFG.refreshHours) > 0 ? Number(CFG.refreshHours) : 24) * 60 * 60 * 1000;

    function setQuiz(answers) {
      var next = get();
      next.quiz = answers ? JSON.parse(JSON.stringify(answers)) : null;
      next.quizAt = answers ? Date.now() : 0;
      return save(next);
    }

    function setFacet(name, value) {
      var next = get();
      next.facets = next.facets || {};
      next.facetOrder = (next.facetOrder || []).filter(function (n) { return n !== name; });
      next.facetAt = next.facetAt || {};
      if (value === null || value === undefined || value === '') {
        delete next.facets[name];
        delete next.facetAt[name];
      } else {
        next.facets[name] = value;
        next.facetAt[name] = Date.now();
        /* Answering again makes it the newest, not the oldest still standing
           — a shopper correcting themselves should not be first out when the
           next answer arrives. */
        next.facetOrder.push(name);
      }
      return save(next);
    }

    function getFacet(name) {
      var p = get();
      return (p.facets && p.facets[name]) || null;
    }

    /* Milliseconds since the facet was answered; null when it never was.
       An answer from before timestamps were kept counts as old: it was
       given on some earlier visit, which is the case a refresh is for. */
    function facetAge(name) {
      var p = get();
      if (!p.facets || !p.facets[name]) return null;
      var at = p.facetAt && p.facetAt[name];
      return at ? Math.max(0, Date.now() - at) : Infinity;
    }

    /* "Fresh" means answered during this visit. A quiz card answered in
       this browser session keeps its Noted bar on every refresh; the next
       session finds it collapsed, asking again, while the answer stays on
       the profile and keeps shaping the page. A caller can still ask for a
       plain age limit with maxAge.

       A signed-in shopper is the exception: their answers are kept with
       their account, so a card they answered stays answered on the next
       visit and the next day too, until they retake it. */
    var SESSION_START = (function () {
      try {
        var v = Number(sessionStorage.getItem('vro_session_start'));
        if (!v) { v = Date.now(); sessionStorage.setItem('vro_session_start', String(v)); }
        return v;
      } catch (e) { return Date.now(); }
    })();

    function isFresh(name, maxAge) {
      var age = facetAge(name);
      if (age === null) return false;
      if (maxAge) return age < maxAge;
      if (owner()) return true;
      return (Date.now() - age) >= SESSION_START;
    }

    function quizAge() {
      var p = get();
      if (!p.quiz) return null;
      return p.quizAt ? Math.max(0, Date.now() - p.quizAt) : Infinity;
    }

    function clear() {
      var next = blank();
      return save(next);
    }

    /* Opt-in server sync. Silent on every failure: personalization is a
       nicety, never a thing that can break the page. */
    function endpoint() {
      if (!CFG.apiBase || !CFG.apiPath) return null;
      return String(CFG.apiBase).replace(/\/+$/, '') + CFG.apiPath;
    }

    function bearer() {
      try {
        if (!window.VroSession || !window.VroSession.load) return null;
        var s = window.VroSession.load();
        return (s && s.token) ? s.token : null;
      } catch (e) { return null; }
    }

    /* The credential the server reads the customer id from. Two kinds of
       sign-in, two kinds of proof:
         - a real Shopify login: window.__vroCustomerSig, signed server-side
           in Liquid (snippets/vro-customer-sig.liquid) for that customer;
         - the phone/OTP sign-in: the session token assets/login-sheet.js
           keeps.
       The Shopify login wins when both exist, matching owner(). The OTP
       token is used for a Shopify-logged-in shopper only when it belongs
       to the same customer, so one account's answers never land on
       another's. */
    function authHeaders() {
      var nativeId = shopifyCustomerId();
      var sig = window.__vroCustomerSig;
      if (nativeId && typeof sig === 'string' && sig.indexOf(nativeId + '.') === 0) {
        return { 'X-Vro-Customer-Sig': sig };
      }
      var token = bearer();
      if (!token) return null;
      if (nativeId && sessionCustomerId() !== nativeId) return null;
      return { Authorization: 'Bearer ' + token };
    }

    /* Has this device reconciled its copy with the account's stored copy
       yet? Until it has, the two are merged rather than one replacing the
       other: a new browser (or an incognito window) that answered a quiz
       as a guest just before signing in would otherwise have its one fresh
       answer overwrite everything the account had saved, simply for being
       newer. After the first reconcile, newest wins as before, so removing
       a card on one device still removes it everywhere. */
    function syncedKey(id) { return STORAGE_PREFIX + ':synced:c' + id; }
    function isSynced(id) {
      try { return localStorage.getItem(syncedKey(id)) === '1'; } catch (e) { return true; }
    }
    function markSynced(id) {
      try { localStorage.setItem(syncedKey(id), '1'); } catch (e) { /* private mode */ }
    }

    /* Account copy first; every facet, memory and quiz answer from this
       device that the account lacks, or holds an older answer for, is laid
       over it. */
    function mergeProfiles(local, remote) {
      var out = JSON.parse(JSON.stringify(remote));
      out.facets = out.facets || {};
      out.facetAt = out.facetAt || {};
      var lf = local.facets || {};
      var la = local.facetAt || {};
      Object.keys(lf).forEach(function (name) {
        var has = Object.prototype.hasOwnProperty.call(out.facets, name);
        if (!has || (la[name] || 0) > (out.facetAt[name] || 0)) {
          out.facets[name] = lf[name];
          if (la[name]) out.facetAt[name] = la[name]; else delete out.facetAt[name];
        }
      });
      var order = (out.facetOrder || []).slice();
      (local.facetOrder || []).forEach(function (n) { if (order.indexOf(n) === -1) order.push(n); });
      out.facetOrder = order;
      if (local.quiz && (!out.quiz || (local.quizAt || 0) > (out.quizAt || 0))) {
        out.quiz = JSON.parse(JSON.stringify(local.quiz));
        out.quizAt = local.quizAt || 0;
      }
      if (local.memories && typeof local.memories === 'object') {
        out.memories = out.memories || {};
        Object.keys(local.memories).forEach(function (k) {
          if (!Object.prototype.hasOwnProperty.call(out.memories, k)) out.memories[k] = local.memories[k];
        });
      }
      return out;
    }

    var pushTimer = null;
    function pushRemote(next, id) {
      var url = endpoint();
      var auth = authHeaders();
      if (!url || !auth || !id) return;
      clearTimeout(pushTimer);
      pushTimer = setTimeout(function () {
        try {
          fetch(url, {
            method: 'PUT',
            headers: Object.assign({ 'Content-Type': 'application/json' }, auth),
            body: JSON.stringify({ profile: next })
          })
            .then(function (res) { return res.ok ? res.json() : null; })
            .then(function (data) {
              /* The server keeps the newest copy. When the one it holds is
                 newer than what this tab sent — answered on the phone since
                 this laptop tab was opened — it hands that one back, and it
                 is what this tab shows from now on. */
              if (data && data.kept === 'stored' && data.profile) adopt(data.profile, id);
            })
            .catch(function () { /* offline or not deployed; local copy stands */ });
        } catch (e) { /* ignore */ }
      }, 600);
    }

    /* A copy from the server becomes this device's copy. It is stored like
       any other, so it is held to the same cap — an older client, or a
       profile written before the cap existed, cannot put a hundred
       memories back on this device. remote: true tells the page it came
       from elsewhere, so a picks row already drawn from the older answer
       redraws. */
    function adopt(remote, id) {
      if (!remote || typeof remote !== 'object') return null;
      if (owner() !== id) return null;   /* signed out or switched meanwhile */
      prune(remote);
      memory = remote;
      memoryOwner = id;
      writeKey(keyFor(id), remote);
      emit('vro:profile:change', { profile: remote, customerId: id, remote: true });
      return remote;
    }

    function pullRemote() {
      var url = endpoint();
      var auth = authHeaders();
      var id = owner();
      if (!url || !auth || !id) return Promise.resolve(null);
      return fetch(url, { headers: auth, cache: 'no-store' })
        .then(function (res) { return res.ok ? res.json() : null; })
        .then(function (data) {
          if (!data || !data.ok) return null;
          if (owner() !== id) return null;   /* signed out or switched meanwhile */
          var remote = (data.profile && typeof data.profile === 'object') ? data.profile : null;
          var local = get();
          var firstSync = !isSynced(id);
          markSynced(id);
          if (firstSync && remote && (local.updatedAt || 0) > 0 && (remote.updatedAt || 0) > 0 &&
              (remote.updatedAt || 0) !== (local.updatedAt || 0)) {
            var merged = mergeProfiles(local, remote);
            prune(merged);
            merged.v = 1;
            merged.updatedAt = Date.now();
            memory = merged;
            memoryOwner = id;
            writeKey(keyFor(id), merged);
            pushRemote(merged, id);
            emit('vro:profile:change', { profile: merged, customerId: id, remote: true });
            return merged;
          }
          /* Newest wins; a stale server copy never overwrites a fresh answer.
             And the other way round: answers given on this device that the
             account has never seen — as a guest just before signing in, or
             before this sync existed — go up now, so the next device finds
             them. */
          if (!remote || (remote.updatedAt || 0) < (local.updatedAt || 0)) {
            if ((local.updatedAt || 0) > 0) pushRemote(local, id);
            return local;
          }
          if ((remote.updatedAt || 0) === (local.updatedAt || 0)) return local;
          return adopt(remote, id);
        })
        .catch(function () { return null; });
    }

    return {
      get: get, save: save, clear: clear,
      setQuiz: setQuiz, setFacet: setFacet, getFacet: getFacet,
      facetAge: facetAge,
      isFresh: isFresh,
      quizAge: quizAge,
      refreshMs: REFRESH_MS,
      customerId: owner, pull: pullRemote,
      /* For assets/profile-memories.js, so the ids it renders under and the
         ids the cap counts are the same ids and not two copies of a rule. */
      memoryIdsFor: memoryIdsFor, memoryCap: MEMORY_CAP
    };
  })();

  function emit(name, detail) {
    try { document.dispatchEvent(new CustomEvent(name, { detail: detail, bubbles: true })); } catch (e) { /* ignore */ }
  }

  /* ---------------------------------------------------------------
     The vocabulary.

     Quiz wording and merchandising wording are not the same words —
     the quiz says "Slow hair growth", the catalogue says "Hair Growth
     Support" on one product and "Supports hair growth" on the next. These
     maps are the translation, kept in one place and reused by every
     surface. Adding a phrase here is how the matching is tuned; nobody has
     to touch a product.
  --------------------------------------------------------------- */
  /* Hair and skin share a parent (Grooming & Care), and the catalogue
     tags some products into both: a Plum pre-wash hair treatment carries
     "Face Wash" beside "Hair Mask", the Khadi herb powders "Face Masks"
     beside "Hair Mask". A scope tag alone let every one of them into a
     Skin result, which is how a skin quiz answered with a hair mask. Each
     of the two departments names the other's tags here, and a product that
     carries any of them is kept out of both rather than shown in the wrong
     one. Only these two: Fashion and Lifestyle share a shelf on purpose. */
  var HAIR_TAGS = ['hair care', 'hair mask', 'hair oil', 'hair serum', 'shampoo conditioners',
                   'hair loss regrowth', 'hair colour', 'hair color', 'hair gels'];
  var SKIN_TAGS = ['face care', 'facecare', 'face wash', 'facewash', 'face serum', 'face masks',
                   'face sunscreen', 'face moisturiser', 'face cream', 'face exfoliators'];

  var CATEGORIES = {
    'Hair & Scalp': {
      collection: 'hair-care',
      scopeTags: ['hair care', 'hair loss regrowth', 'shampoo conditioners', 'hair oil', 'hair serum', 'hair mask', 'hair colour', 'hair color root touchups'],
      /* The other department's tags. A product that carries both is not
         a hair answer the shopper can trust — see inDepartment(). */
      excludeTags: SKIN_TAGS,
      typeField: 'hair',
      label: 'hair & scalp'
    },
    'Skin': {
      collection: 'face-care',
      scopeTags: ['face care', 'facecare', 'face wash', 'facewash', 'face serum', 'face masks', 'face sunscreen', 'face moisturiser', 'face cream', 'face exfoliators', 'body care'],
      excludeTags: HAIR_TAGS,
      typeField: 'skin',
      label: 'skin'
    },
    'Health & Energy': {
      collection: 'health-nutrition',
      scopeTags: ['health nutrition', 'daily wellness', 'herbal wellness', 'protein muscle', 'multivitamins', 'immunity', 'fitness fuel', 'ayurveda', 'joint support', 'vitamins minerals'],
      typeField: null,
      label: 'health & energy'
    },
    'Fashion & Styling': {
      collection: 'fashion-lifestyle',
      scopeTags: ['fashion lifestyle', 'clothing accessories', 't shirts polos shirts', 't shirts', 'tshirts', 'topwear', 'bottomwear', 'jeans', 'trousers', 'footwear', 'casual shirts'],
      typeField: null,
      label: 'fashion & styling',
      /* No concern metafields exist on apparel, so the honest rule here is
         scope + budget rather than a pretend relevance score. */
      openMatch: true
    },
    /* Fragrance sits inside Grooming & Care in the shop's own tree, but a
       shopper picking a scent is not answering a grooming question: the
       vibe, the boldness and how long it lasts are its own four layers.
       openMatch because perfume carries no concern metafields — the vibe
       ranks the shelf rather than gating it, and a scope-and-budget answer
       is the honest one here. */
    'Fragrance': {
      collection: 'fragrance',
      scopeTags: ['fragrance', 'fragrances', 'perfume', 'perfumes', 'perfumes body mists', 'body mist', 'attar', 'deodorant', 'deodorants', 'fragrance sets', 'eau de parfum', 'eau de toilette'],
      typeField: null,
      label: 'fragrance',
      openMatch: true
    },
    /* Watches, footwear and bags — the gear quiz (sheet: What's Bugging —
       Lifestyle). Its picks come from the team's sheet
       (assets/vro-quiz-picks.js); this entry is what lets the engine
       recognise the department and fall back to the shelf if it has to. */
    'Lifestyle': {
      collection: 'fashion-lifestyle',
      scopeTags: ['watches', 'watch', 'footwear', 'shoes', 'sneakers', 'loafers', 'sandals', 'bags', 'backpacks', 'duffel bags', 'luggage', 'trolley bags', 'sling bags'],
      typeField: null,
      label: 'lifestyle',
      openMatch: true
    },
    'Technology': {
      collection: 'tech-gadgets',
      scopeTags: ['tech gadgets', 'tech accessories', 'powers chargers', 'power bank', 'charger', 'headphone', 'earbuds', 'smart watch', 'gadget'],
      typeField: null,
      label: 'technology',
      openMatch: true
    }
  };

  /* problem -> phrases that appear in the store's concern/benefit fields */
  var PROBLEM_SIGNALS = {
    'hairfall':          ['hair fall', 'hairfall', 'hair loss', 'hair thinning', 'root strengthening', 'anti hair fall', 'thickening'],
    /* The same concern, spelled the way the quiz sheet spells it. Keyed
       rather than aliased because norm() keeps the space, and the chips
       elsewhere in the theme still say "Hairfall". */
    'hair fall':         ['hair fall', 'hairfall', 'hair loss', 'hair thinning', 'root strengthening', 'anti hair fall', 'thickening'],
    'frizzy hair':       ['frizz', 'anti frizz', 'frizz control', 'smoothening', 'smooth', 'keratin', 'conditioning', 'nourishment'],
    /* Fashion: what the shopper is upgrading. */
    'everyday wear':     ['t shirt', 'tshirt', 'tee', 'polo', 'casual', 'everyday', 'basic', 'jeans', 'joggers', 'shorts'],
    'smartwear':         ['formal', 'smart', 'blazer', 'shirt', 'trouser', 'chino', 'office', 'suit'],
    'footwear':          ['footwear', 'shoe', 'sneaker', 'loafer', 'sandal', 'slipper', 'boot', 'flip flop'],
    'watches':           ['watch', 'watches', 'timepiece', 'chronograph', 'smart watch'],
    'bags':              ['bag', 'bags', 'backpack', 'duffle', 'sling', 'wallet', 'luggage', 'tote'],
    /* Fragrance: the vibe. */
    'fresh':             ['fresh', 'citrus', 'aqua', 'aquatic', 'marine', 'cool', 'breeze', 'lemon', 'bergamot'],
    'woody':             ['woody', 'wood', 'sandalwood', 'cedar', 'vetiver', 'oud', 'patchouli'],
    'oriental':          ['oriental', 'spice', 'spicy', 'amber', 'vanilla', 'exotic'],
    'oud':               ['oud', 'agarwood', 'attar', 'arabic', 'oudh'],
    'musky':             ['musk', 'musky', 'amber', 'powdery', 'skin scent'],
    'slow hair growth':  ['hair growth', 'growth support', 'supports hair growth', 'regrowth', 'hair thinning', 'root strengthening', 'thickening', 'follicle'],
    'dandruff':          ['dandruff', 'anti dandruff', 'flaky', 'scalp detox', 'deep scalp cleansing', 'dry scalp relief', 'anti fungal'],
    'itchy scalp':       ['itchy', 'itch', 'scalp relief', 'dry scalp', 'scalp soothing', 'scalp detox', 'deep scalp cleansing', 'dandruff'],
    'split ends':        ['split end', 'damage repair', 'frizz control', 'smoothening', 'conditioning', 'nourishment', 'keratin'],
    'sleep':             ['sleep', 'melatonin', 'relax', 'calming', 'stress', 'insomnia'],
    'dry skin':          ['dryness', 'hydration', 'moistur', 'nourishment', 'soothing'],
    'acne':              ['acne', 'blemish', 'pimple', 'breakout', 'oil control', 'texture refining', 'salicylic'],
    'pigmentation':      ['pigmentation', 'dark spot', 'brightening', 'glow boosting', 'dullness', 'even tone'],
    'dark spots':        ['dark spot', 'pigmentation', 'brightening', 'glow boosting', 'dullness', 'even tone'],
    'dryness':           ['dryness', 'hydration', 'moistur', 'nourishment', 'soothing'],
    'oiliness':          ['oil control', 'oily', 'mattif', 'detoxifying', 'pore', 'sebum'],
    'sunburn':           ['sun protection', 'spf', 'sunscreen', 'uv', 'after sun', 'soothing', 'calming'],
    'fine lines':        ['fine line', 'anti ageing', 'anti aging', 'wrinkle', 'firming', 'collagen', 'skin renewal', 'retinol'],
    'low energy':        ['energy', 'stamina', 'vitality', 'endurance', 'pre workout', 'fatigue'],
    'poor sleep':        ['sleep', 'melatonin', 'relax', 'calm', 'stress', 'insomnia'],
    'stress':            ['stress', 'calm', 'relax', 'ashwagandha', 'mood', 'anxiety'],
    'weight goals':      ['weight', 'fat', 'lean', 'metabolism', 'muscle', 'protein', 'slimming'],
    'immunity':          ['immun', 'vitamin c', 'antioxidant', 'wellness', 'cold relief'],
    'digestion':         ['digest', 'gut', 'probiotic', 'fibre', 'fiber', 'bloat', 'constipation'],
    'fit issues':        ['fit', 'stretch', 'regular fit', 'slim fit', 'relaxed fit', 'oversized'],
    'what to pair':      ['casual', 'formal', 'occasion', 'everyday'],
    'building a wardrobe': ['essential', 'basic', 'everyday', 'staple'],
    'battery life':      ['battery', 'mah', 'power bank', 'charging', 'fast charge'],
    'screen protection': ['screen', 'tempered', 'protector', 'scratch', 'case', 'cover']
  };

  /* ---------------------------------------------------------------
     The quiz's own words, in one place.

     These lists used to be written out again in assets/quiz.js,
     sections/quiz-cta-banner.liquid and sections/sort-a-concern.liquid —
     four copies of the same vocabulary, and they drifted. One quiz called
     the category "Technology & Gadgets" while CATEGORIES here calls it
     "Technology", so the lookup missed, the engine answered "no-category",
     and the surface fell back to a plain keyword search: a shopper asking
     about screen protection was shown two shirts and a peeling gel,
     because all three cost between a thousand and fifteen hundred rupees
     and nothing else was ever checked.

     A category name that does not appear in CATEGORIES cannot match
     anything, so the names live next to CATEGORIES and every surface reads
     them from here.
     --------------------------------------------------------------- */
  /* Other names the same department goes by. The chip a shopper taps is
     merchant copy — a label in the theme editor — so it is not the code's
     to dictate: the Fashion chip reads "Fashion & Styling" in one quiz and
     the shop calls that department "Fashion & Lifestyle", and the tech chip
     reads "Technology & Gadgets" where CATEGORIES calls it "Technology".
     An exact-key lookup answered "no-category" to that and the surface fell
     back to a plain keyword search. Matching on the words instead means a
     merchant can rename a chip without silently turning the matching off. */
  var CATEGORY_ALIASES = {
    'Hair & Scalp':      ['hair', 'hair scalp', 'hair care', 'haircare', 'scalp'],
    'Skin':              ['skin', 'skincare', 'skin care', 'face', 'face care', 'grooming', 'grooming care'],
    'Health & Energy':   ['health', 'health energy', 'health nutrition', 'nutrition', 'wellness', 'supplements'],
    'Fashion & Styling': ['fashion', 'fashion styling', 'fashion lifestyle', 'style', 'clothing', 'apparel'],
    'Fragrance':         ['fragrance', 'fragrances', 'perfume', 'perfumes', 'scent', 'scents', 'attar', 'deodorant'],
    'Lifestyle':         ['lifestyle', 'gear', 'accessories', 'watches', 'bags'],
    'Technology':        ['tech', 'technology', 'technology gadgets', 'tech gadgets', 'gadgets', 'electronics']
  };

  var PROBLEM_SETS = {
    /* The sheet's four, in its words. "Slow hair growth" and "Split ends"
       went with it: four options is what the card is drawn for, and both
       are answered by the two that stayed. */
    'Hair & Scalp':      ['Hair fall', 'Dandruff', 'Frizzy hair', 'Itchy scalp'],
    'Skin':              ['Acne', 'Dark spots', 'Dryness', 'Oiliness', 'Sunburn', 'Fine lines'],
    'Health & Energy':   ['Low energy', 'Poor sleep', 'Stress', 'Weight goals', 'Immunity', 'Digestion'],
    'Fashion & Styling': ['Everyday Wear', 'Smartwear', 'Party Fits', 'Weekend Fits', 'Statement Looks'],
    'Lifestyle':         ['Watches', 'Footwear', 'Bags'],
    'Fragrance':         ['Fresh', 'Woody', 'Oriental', 'Oud', 'Musky'],
    'Technology':        ['Battery life', 'Screen protection']
  };

  /* The first question's wording, per department. Hair is asked for a
     problem; fashion is asked what it is upgrading and fragrance what it
     smells like, and calling either of those "your fashion problem" is the
     wrong question in the wrong words. A department with no line here
     keeps the old phrasing. */
  var PROBLEM_TITLES = {
    'Hair & Scalp':      'Pick your hair problem',
    'Fashion & Styling': "What's the upgrade?",
    'Lifestyle':         'Pick your gear',
    'Fragrance':         "What's your scent vibe?"
  };

  var CONDITION_SETS = {
    'Hair & Scalp': { title: "How's your scalp behaving?", options: ['Oily', 'Dry', 'Normal', 'All hair types'] },
    'Skin':         { title: "How's your skin right now",  options: ['Oily', 'Dry', 'Combination', 'Sensitive', 'Normal'] },
    'Fashion & Styling': { title: 'Pick your style lane', options: ['Classic', 'Contemporary', 'Sporty', 'Street', 'Minimal'] },
    'Lifestyle':    { title: "What's the occasion?", options: ['Everyday', 'Work', 'Party', 'Weekend', 'Active'] },
    'Fragrance':    { title: 'How bold do you want to go?', options: ['Mild', 'Medium', 'Strong', 'Long-Lasting', 'Intense'] },
    'default':      { title: 'How would you describe this', options: ['Mild', 'Moderate', 'Frequent', 'Not sure'] }
  };

  /* The one question that is asked of some departments and not of others.

     Hair has a type as well as a state — a scalp is oily or dry today, but
     the hair on it is straight or curly always — and the two are different
     questions with different answers. A department with no entry here is
     asked three questions as before; the card skips the step rather than
     showing an empty one. */
  var TRAIT_SETS = {
    'Hair & Scalp':      { title: "What's your hair type?", options: ['Straight', 'Curly', 'Wavy', 'Thick'] },
    'Fashion & Styling': { title: 'Pick your fabric', options: ['Cotton', 'Linen', 'Denim', 'Bamboo', 'Blend'] },
    'Lifestyle':         { title: 'Pick your style lane', options: ['Classic', 'Contemporary', 'Sporty', 'Street', 'Minimal'] },
    'Fragrance':         { title: 'How long should it stay with you?', options: ['4-6 hours', '6-8 hours', '8+ hours'] }
  };

  /* A trait never gates a product out: a curl cream is a better answer for
     curly hair than a plain shampoo, but the shampoo is not the wrong
     answer. It ranks, it does not filter. */
  var TRAIT_SIGNALS = {
    /* Keys are normalized (norm() turns "4-6 hours" into "4 6 hours" and
       "Long-Lasting" into "long lasting"), because that is what they are
       looked up by. */
    'cotton':     ['cotton', 'poly cotton', 'polycotton'],
    'linen':      ['linen'],
    'denim':      ['denim', 'jeans'],
    'leather':    ['leather', 'genuine leather', 'faux leather', 'suede'],
    '4 6 hours':  ['body mist', 'cologne', 'eau de cologne', 'light', 'mist'],
    '6 8 hours':  ['eau de toilette', 'edt', 'long lasting', 'lasting'],
    '8 hours':    ['eau de parfum', 'edp', 'extrait', 'parfum', 'long lasting', '24 hour', 'all day', 'intense'],
    'straight': ['straight hair', 'straight', 'smoothening', 'anti frizz'],
    'curly':    ['curly', 'curl', 'coil', 'wavy', 'curl defining'],
    'wavy':     ['wavy', 'wave', 'curly', 'curl'],
    'thick':    ['thick', 'thickening', 'volumis', 'volume', 'dense', 'coarse']
  };

  /* condition -> the types it rules out, and the phrases it rewards */
  var CONDITIONS = {
    'oily':        { avoidTypes: ['dry'], boost: ['oil control', 'mattif', 'detox', 'purifying', 'pore', 'gel'] },
    'dry':         { avoidTypes: ['oily'], boost: ['hydration', 'moistur', 'nourish', 'dry scalp', 'cream', 'butter'] },
    'combination': { avoidTypes: [], boost: ['balancing', 'all skin types', 'gentle'] },
    'sensitive':   { avoidTypes: [], boost: ['soothing', 'calming', 'gentle', 'mild', 'fragrance free', 'dermatologically tested', 'hypoallergenic'] },
    'flaky':       { avoidTypes: ['oily'], boost: ['dandruff', 'flaky', 'scalp detox', 'dry scalp relief', 'exfoliat'] },
    'normal':      { avoidTypes: [], boost: [] },
    'mild':        { avoidTypes: [], boost: ['mild', 'light', 'subtle', 'gentle', 'body mist', 'cologne'] },
    /* "Suits everyone", said as an answer. It rules nothing out — that is
       the point of picking it — and rewards a product that says the same
       of itself. */
    'all hair types': { avoidTypes: [], boost: ['all hair types', 'all hair type', 'suitable for all'] },

    /* Fashion's style lanes. Nothing to rule out — a lane is a preference,
       not an allergy — so each only says what it rewards. */
    'classic':      { avoidTypes: [], boost: ['classic', 'regular fit', 'formal', 'timeless', 'solid', 'straight fit'] },
    'contemporary': { avoidTypes: [], boost: ['contemporary', 'modern', 'slim fit', 'tapered', 'trend'] },
    'sporty':       { avoidTypes: [], boost: ['sport', 'active', 'athleisure', 'training', 'running', 'gym', 'dri fit'] },
    'street':       { avoidTypes: [], boost: ['street', 'oversized', 'graphic', 'baggy', 'urban', 'relaxed fit'] },
    'minimal':      { avoidTypes: [], boost: ['minimal', 'solid', 'plain', 'basic', 'essential', 'plain solid'] },

    /* Fragrance, on how loud it should be. */
    'medium':       { avoidTypes: [], boost: ['eau de toilette', 'edt', 'medium', 'moderate'] },
    'strong':       { avoidTypes: [], boost: ['eau de parfum', 'edp', 'strong', 'intense', 'bold', 'concentrated'] },
    'long lasting': { avoidTypes: [], boost: ['long lasting', '24 hour', 'all day', 'lasting', 'eau de parfum', 'edp'] },
    'intense':      { avoidTypes: [], boost: ['intense', 'extrait', 'parfum', 'concentrated', 'bold', 'oud'] },

    /* The four nutrition goals. `avoid` is the half the skin conditions
       never needed: two of these goals are opposites, and a mass gainer is
       not merely a weak answer to "losing weight", it is the wrong one —
       so it scores below nothing rather than above it and is taken out of
       the row even when nothing better is there. Matched against the
       card's own signals, benefits and tags, which is where this catalogue
       actually says what a product is for. */
    'muscle':      { avoidTypes: [], avoid: ['weight loss', 'fat burner', 'slimming', 'appetite control'],
                     boost: ['muscle', 'lean mass', 'protein synthesis', 'strength', 'whey', 'casein',
                             'creatine', 'bcaa', 'eaa', 'recovery', 'nitrogen', 'performance'] },
    'gain':        { avoidTypes: [], avoid: ['weight loss', 'fat burner', 'slimming', 'low calorie', 'appetite control'],
                     boost: ['weight gain', 'mass gainer', 'gainer', 'high calorie', 'calorie',
                             'bulk', 'weight gainer', 'appetite'] },
    /* No bare 'lean' in the boosts: half the protein shelf says "lean
       muscle growth", and that word alone put whey isolate in front of a
       shopper who had just said they were trying to lose weight. */
    'lean':        { avoidTypes: [], avoid: ['mass gainer', 'weight gain', 'gainer', 'high calorie', 'bulk'],
                     boost: ['fat loss', 'fat burner', 'weight management', 'metabolism', 'slimming',
                             'l-carnitine', 'garcinia', 'green tea', 'low calorie', 'detox'] },
    'wellness':    { avoidTypes: [], avoid: [],
                     boost: ['immunity', 'multivitamin', 'vitamin', 'mineral', 'antioxidant', 'omega',
                             'gut health', 'probiotic', 'daily wellness', 'wellbeing', 'general health',
                             'energy', 'sleep', 'stress'] }
  };

  /* Types that mean "suits everyone" and therefore never conflict. */
  var UNIVERSAL_TYPES = ['all', 'all skin types', 'all hair types', 'normal', 'every', 'unisex'];

  var BUDGETS = {
    'Below ₹500':      [0, 50000],
    '₹500 - ₹1000':    [50000, 100000],
    '₹1000 - ₹1500':   [100000, 150000],
    '₹1500 - ₹2000':   [150000, 200000],
    /* Open-ended at the top, and it has to be a real number rather than
       Infinity: this map is also the quiz's own vocabulary
       (Object.keys(BUDGETS) below), and a band that is missing here is not
       a band that fails to match — `BUDGETS[answers.budget]` comes back
       undefined and the price gate in scoreCard() is skipped entirely, so
       "Above ₹2000" was returning ₹199 face washes. ₹1,00,000 is past
       anything the store sells. */
    'Above ₹2000':     [200000, 100000000],

    /* The sheet's four bands, which are what the quizzes offer now. The
       five above stay in the map rather than being replaced: a shopper
       who answered before this change has one of those words saved in
       their profile, and a band that is missing here is not a band that
       fails to match — it is a price gate silently skipped. */
    'Under ₹500':        [0, 50000],
    '₹500 - ₹1,000':     [50000, 100000],
    '₹1,000 - ₹1,500':   [100000, 150000],
    'Above ₹1,500':      [150000, 100000000],

    /* Fashion's bands: a shirt and a shampoo are not priced on the same
       ladder, and "Above ₹1,500" is most of the apparel catalogue. */
    'Under ₹1,000':      [0, 100000],
    '₹1,000 - ₹2,500':   [100000, 250000],
    '₹2,500 - ₹5,000':   [250000, 500000],
    '₹5,000 - ₹10,000':  [500000, 1000000],
    'Above ₹10,000':     [1000000, 100000000],

    /* Fragrance's. */
    '₹1,000 - ₹2,000':   [100000, 200000],
    '₹2,000 - ₹3,500':   [200000, 350000],
    'Above ₹3,500':      [350000, 100000000],
    /* Fashion's revised ladder and Lifestyle's (the sheets' words). */
    '₹1,000–₹1,500':     [100000, 150000],
    '₹1,500–₹2,000':     [150000, 200000],
    '₹2,000–₹3,000':     [200000, 300000],
    '₹3,000+':           [300000, 100000000],
    'Under ₹2,000':      [0, 200000],
    '₹3,000–₹5,000':     [300000, 500000],
    '₹5,000+':           [500000, 100000000]
  };

  /* What a quiz offers, as against what the map can still read. Each
     department is asked about the money in its own ladder; anything
     without a list of its own is asked the general four. */
  var BUDGET_OPTIONS = ['Under ₹500', '₹500 - ₹1,000', '₹1,000 - ₹1,500', 'Above ₹1,500'];

  /* And what the money question is called. The sheets word it per
     department — the shop asks fashion what the spend is and fragrance
     what it is looking to spend — so the wording travels with the bands. */
  var BUDGET_TITLES = {
    'Fashion & Styling': "What's your budget?",
    'Lifestyle':         "What's the spend?",
    'Fragrance':         'What are you looking to spend?'
  };

  var BUDGET_SETS = {
    'Fashion & Styling': ['Under ₹1,000', '₹1,000–₹1,500', '₹1,500–₹2,000', '₹2,000–₹3,000', '₹3,000+'],
    'Lifestyle':         ['Under ₹2,000', '₹2,000–₹3,000', '₹3,000–₹5,000', '₹5,000+'],
    'Fragrance':         ['Under ₹500', '₹500 - ₹1,000', '₹1,000 - ₹2,000', '₹2,000 - ₹3,500', 'Above ₹3,500']
  };

  /* The in-section facet cards ask the same questions in shopper words. */
  var FACET_VALUES = {
    skin: {
      'gets oily by noon':    'oily',
      'feels tight and dry':  'dry',
      'a bit of both':        'combination',
      'combination skin':     'combination',
      'reacts to most things':'sensitive'
    },
    /* "What are you working towards", on Health & Nutrition. A goal, not a
       type: nothing on a tub of whey declares who it is for, so these are
       answered from what the product says it does. */
    goal: {
      'building muscle': 'muscle',
      'build muscle':    'muscle',
      'gaining weight':  'gain',
      'gain weight':     'gain',
      'losing weight':   'lean',
      'lose weight':     'lean',
      'staying healthy': 'wellness',
      'stay healthy':    'wellness',
      'general health':  'wellness'
    }
  };

  /* ---------------------------------------------------------------
     What a row is ABOUT.

     A tab is labelled "SPF" but wired to the razors collection; another
     says "Moisturiser" and points at body butter. The label is the honest
     statement of intent — it is what the shopper reads — so it, not the
     collection, decides what may appear in that row.

     The store's tags are already good merchandising vocabulary ("Face
     Sunscreen", "Beardcare", "Shampoo & Conditioners"), so the topic is
     matched against a card's tags and type. The map below only bridges the
     gap between how a tab is labelled and how the catalogue is tagged;
     anything not listed still works, on the label itself.
  --------------------------------------------------------------- */
  var TOPIC_SYNONYMS = {
    'spf': ['spf', 'sunscreen', 'sun protection', 'face sunscreen', 'sun care'],
    'sunscreen': ['sunscreen', 'spf', 'sun protection', 'face sunscreen'],
    'face wash': ['face wash', 'facewash', 'cleanser', 'face cleanser', 'facial cleanser'],
    'facewash': ['face wash', 'facewash', 'cleanser'],
    'cleanser': ['cleanser', 'face wash', 'facewash'],
    'moisturiser': ['moisturiser', 'moisturizer', 'face moisturiser', 'body moisturisers', 'body lotion', 'body butter', 'face cream', 'day cream', 'night cream'],
    'moisturizer': ['moisturiser', 'moisturizer', 'face moisturiser', 'body moisturisers', 'body lotion', 'body butter', 'face cream'],
    'beard': ['beard', 'beardcare', 'beard oil', 'shaving', 'razor', 'trimmer'],
    'shave': ['shaving', 'razor', 'beardcare', 'shave'],
    'serum': ['serum', 'face serum', 'hair serum'],
    'shampoo': ['shampoo', 'shampoo & conditioners', 'conditioner'],
    'conditioner': ['conditioner', 'shampoo & conditioners'],
    'hair oil': ['hair oil', 'hair care'],
    'mask': ['mask', 'face masks', 'hair mask'],
    'toner': ['toner', 'facemist', 'face mist'],
    'sunglasses': ['sunglasses', 'eyewear', 'shades'],
    't-shirts': ['t shirt', 'tshirt', 't shirts', 'tshirts', 'topwear'],
    /* Apparel tabs the shop labels one way and tags another: the "Tanks"
       tab points at the vests collection, "Layers" at rain jackets, "Caps"
       at caps & hats. Without these the tab's own word appears nowhere on
       its products and the topic gate threw out every one of them. */
    'tanks': ['tank', 'tanks', 'vest', 'vests', 'sleeveless', 'innerwear'],
    'tank': ['tank', 'vest', 'vests', 'sleeveless'],
    'vests': ['vest', 'vests', 'tank', 'innerwear'],
    'layers': ['jacket', 'jackets', 'rain jacket', 'rain jackets', 'hoodie', 'sweatshirt', 'overshirt', 'winterwear', 'rainsuit'],
    'shorts': ['short', 'shorts', 'bottomwear'],
    'caps': ['cap', 'caps', 'hat', 'hats', 'caps hats'],
    'socks': ['sock', 'socks', 'ankle socks'],
    't shirts': ['t shirt', 'tshirt', 'tshirts', 'topwear'],
    'tshirts': ['t shirt', 'tshirt', 'tshirts', 'topwear'],
    'shirts': ['shirt', 'shirts', 'casual shirts', 'topwear'],
    'jeans': ['jeans', 'denim'],
    'trousers': ['trousers', 'pants', 'chinos', 'bottomwear'],
    'slippers': ['slipper', 'slippers', 'flip flop', 'sandal', 'casual shoes', 'footwear'],
    'shoes': ['shoes', 'sneaker', 'footwear', 'casual shoes'],
    'watches': ['watch', 'watches', 'smart watch', 'smartwatch'],
    'protein': ['protein', 'whey protein', 'plant protein', 'protein & muscle'],
    'vitamins': ['vitamin', 'multivitamins', 'vitamins minerals & micros'],
    'immunity': ['immunity', 'immune', 'daily wellness']
  };

  /* Words that describe nothing on their own. "Care" is on half the
     catalogue; "view all" is not a topic at all. */
  var TOPIC_STOPWORDS = ['and', 'the', 'for', 'all', 'view', 'view all', 'new', 'shop', 'more', 'care', 'thing', 'things'];

  function topicPhrases(label) {
    var clean = norm(label);
    if (!clean) return [];

    var out = [];
    function add(phrase) {
      var p = norm(phrase);
      if (p && TOPIC_STOPWORDS.indexOf(p) === -1 && out.indexOf(p) === -1) out.push(p);
    }

    var known = TOPIC_SYNONYMS[clean];
    if (known) known.forEach(add);
    add(clean);
    add(clean.replace(/\s+/g, ''));          /* "face wash" also tagged "facewash" */

    /* Falling back word by word only when the whole label is unknown. A
       label that IS in the map is already precise, and breaking it up
       would undo that: "Face Wash" would gain the bare word "face" and
       quietly widen to every face product on the store, serums included. */
    if (!known) {
      clean.split(' ').forEach(function (word) {
        if (TOPIC_STOPWORDS.indexOf(word) > -1 || word.length < 3) return;
        if (TOPIC_SYNONYMS[word]) TOPIC_SYNONYMS[word].forEach(add);
        else add(word);
      });
    }

    return out;
  }

  /* The gate itself: does this product belong in a row about that? */
  function matchesTopic(card, phrases) {
    if (!phrases || !phrases.length) return true;
    var data = cardData(card);
    if (!data) return false;
    return includesAny(data.tags + ' ~ ' + data.signals, phrases);
  }

  /* ---------------------------------------------------------------
     Reading a card
  --------------------------------------------------------------- */
  function cardData(card) {
    if (!card) return null;
    if (card.__vro) return card.__vro;
    var data = {
      handle: card.getAttribute('data-vro-handle') || '',
      price: parseInt(card.getAttribute('data-vro-price'), 10),
      tags: norm(card.getAttribute('data-vro-tags') || ''),
      signals: norm(card.getAttribute('data-vro-signals') || ''),
      benefits: norm(card.getAttribute('data-vro-benefits') || ''),
      ingredients: norm(card.getAttribute('data-vro-ingredients') || ''),
      skinType: norm(card.getAttribute('data-vro-skin-type') || ''),
      hairType: norm(card.getAttribute('data-vro-hair-type') || '')
    };
    data.blob = data.signals + ' ~ ' + data.tags;
    card.__vro = data;
    return data;
  }

  function typeFor(data, which) {
    if (which === 'hair') return data.hairType || data.skinType;
    if (which === 'skin') return data.skinType || data.hairType;
    return '';
  }

  function typeIsUniversal(value) {
    if (!value) return true;
    return includesAny(value, UNIVERSAL_TYPES);
  }

  /* ---------------------------------------------------------------
     The rule itself.

     A card is kept only when every hard gate passes:
       price inside the chosen band, exactly;
       at least one chosen problem genuinely matched (unless the category
         has no concern data at all, where scope + budget is the honest
         answer rather than a made-up score);
       nothing about the declared skin/hair type contradicts the condition.
     Everything else only orders what survived.
  --------------------------------------------------------------- */
  /* Is this product in the department the shopper picked? The store tags
     by department, so the tag is the gate — and counting what passes it is
     also how we tell "we stock none of this" apart from "we stock it, but
     nothing answers your question". Those deserve different sentences: no
     budget a shopper widens will conjure a department the shop has never
     carried. */
  function inDepartment(data, cat) {
    if (inOtherDepartment(data, cat)) return false;
    if (!cat || !cat.scopeTags || !cat.scopeTags.length) return true;
    return includesAny(data.tags, cat.scopeTags);
  }

  /* Carries another department's tags (CATEGORIES excludeTags) — a hair
     mask in a Skin result. Its own check so the team's sheeted picks, which
     are not held to scopeTags, can still be held to this. */
  function inOtherDepartment(data, cat) {
    if (!data || !cat || !cat.excludeTags || !cat.excludeTags.length) return false;
    return includesAny(data.tags, cat.excludeTags);
  }

  function scoreCard(card, answers, category) {
    var data = cardData(card);
    if (!data) return null;

    var range = BUDGETS[answers.budget];
    if (range && isFinite(data.price)) {
      if (data.price < range[0] || data.price > range[1]) return null;
    } else if (range && !isFinite(data.price)) {
      return null;                       /* no price to check: cannot promise the band */
    }

    var cat = category || {};

    /* Now that the pool reaches the whole inventory and not just one shelf,
       the category has to be enforced rather than assumed. A face cream
       genuinely addresses "dryness", but it does not belong in a Hair &
       Scalp result, and search would happily offer it. The store tags its
       products by department, so that is the gate. */
    if (!inDepartment(data, cat)) return null;

    var problems = answers.problems || [];
    var matched = [];
    /* How many of the chosen words this build actually knows phrases for.
       The quiz's options are editable in the admin now, so a shopper can
       pick a word the catalogue has never been taught — and gating on a
       word we cannot look for is how a whole shelf comes back empty. An
       unknown answer constrains nothing; a known one still has to be
       met. */
    var known = 0;

    for (var i = 0; i < problems.length; i++) {
      var phrases = PROBLEM_SIGNALS[norm(problems[i])];
      if (!phrases) continue;
      known++;
      if (includesAny(data.blob, phrases)) matched.push(problems[i]);
    }

    if (!cat.openMatch && known && !matched.length) return null;

    var condition = norm(answers.condition);
    var rule = CONDITIONS[condition];
    if (rule && cat.typeField) {
      var declared = typeFor(data, cat.typeField);
      if (declared && !typeIsUniversal(declared) && includesAny(declared, rule.avoidTypes)) return null;
    }

    /* Weighted towards what the product claims to DO. Hair Concern reads
       "Hair Fall" on three quarters of the hair catalogue, hair colours
       included, so a product that matches only there is a weaker answer
       than one whose Key Benefits actually name the thing asked about. */
    var score = matched.length * 10;
    for (var b = 0; b < matched.length; b++) {
      var benefitPhrases = PROBLEM_SIGNALS[norm(matched[b])];
      if (benefitPhrases && includesAny(data.benefits, benefitPhrases)) score += 6;
    }
    if (rule) {
      if (rule.boost.length && includesAny(data.blob, rule.boost)) score += 4;
      var declaredType = cat.typeField ? typeFor(data, cat.typeField) : '';
      if (declaredType && declaredType.indexOf(condition) > -1) score += 3;
    }
    var traitPhrases = TRAIT_SIGNALS[norm(answers.trait)];
    if (traitPhrases && includesAny(data.blob, traitPhrases)) score += 3;
    if (card.getAttribute('data-vro-available') === 'true') score += 1;

    return { card: card, score: score, matched: matched };
  }

  /* ---------------------------------------------------------------
     The candidate pool
  --------------------------------------------------------------- */
  /* The cache holds the fetched HTML, never the parsed cards. Elements are
     live: rendering a result moves them into the page, so handing the same
     ones out twice would steal the cards from the rail that already has
     them. Re-parsing per call is cheap and always gives a clean set. */
  var poolCache = {};

  /* A source is either a shelf the merchant curated or a keyword search of
     the whole inventory. Same fragment shape, same cards, so everything
     downstream treats them identically. */
  /* source.filters: the listing page's own grid, rebuilt for a quiz
     answer (?vro=, assets/vro-plp.js). It draws from the shelf as the
     shopper has filtered and sorted it — the filter.* and sort_by in the
     address bar — rather than the whole shelf, which put products the
     applied filters exclude back on screen under a bar saying otherwise. */
  function pageFilters() {
    var out = [];
    new URLSearchParams(window.location.search).forEach(function (v, k) {
      if (v !== '' && (k.indexOf('filter.') === 0 || k === 'sort_by')) out.push(encodeURIComponent(k) + '=' + encodeURIComponent(v));
    });
    return out.length ? '&' + out.join('&') : '';
  }

  function sourceUrl(source, page) {
    if (source && source.search) {
      return '/search?q=' + encodeURIComponent(source.search) +
             '&type=product&view=vro-personalize&page=' + page;
    }
    return '/collections/' + encodeURIComponent(source.collection) +
           '?view=vro-personalize&page=' + page + (source.filters ? pageFilters() : '');
  }

  function sourceKey(source) {
    return source && source.search ? 's:' + source.search : 'c:' + source.collection + (source.filters ? pageFilters() : '');
  }

  function fetchPoolPage(source, page) {
    var key = sourceKey(source) + '#' + page;

    if (!poolCache[key]) {
      var url = sourceUrl(source, page);
      poolCache[key] = fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (res) {
          if (!res.ok) throw new Error('pool ' + res.status);
          return res.text();
        })
        .catch(function (err) {
          delete poolCache[key];               /* a failure must not be cached */
          throw err;
        });
    }

    return poolCache[key].then(function (html) {
      var holder = document.createElement('div');
      holder.innerHTML = html;
      var meta = holder.querySelector('[data-vro-pool]');
      return {
        cards: Array.prototype.slice.call(holder.querySelectorAll('.feed-card')),
        pages: meta ? parseInt(meta.getAttribute('data-vro-pages'), 10) || 1 : 1
      };
    });
  }

  function fetchSource(source, maxPages) {
    return fetchPoolPage(source, 1).then(function (first) {
      var pages = Math.min(first.pages || 1, maxPages || POOL_MAX_PAGES);
      if (pages < 2) return first.cards.slice();

      var rest = [];
      for (var p = 2; p <= pages; p++) rest.push(fetchPoolPage(source, p));

      return Promise.all(rest.map(function (promise) {
        return promise.catch(function () { return { cards: [] }; });
      })).then(function (results) {
        var all = first.cards.slice();
        results.forEach(function (r) { all = all.concat(r.cards); });
        return all;
      });
    }).catch(function () { return []; });
  }

  /* The pool a row is built from: the merchant's shelf first, then the rest
     of the inventory by keyword. Two reasons for the second half — a tab
     can be pointed at the wrong collection, and a collection can simply not
     contain the thing the row is about. Search is recall only; every card
     it returns still has to pass the topic gate and the structured scoring
     before it can appear, so widening the net does not loosen the result.
     Order matters: the merchant's own shelf is seen first, so on an equal
     score it wins. */
  function fetchPool(source, maxPages) {
    var sources = [];
    if (typeof source === 'string') sources.push({ collection: source });
    else if (Array.isArray(source)) sources = source.slice();
    else if (source) sources.push(source);

    sources = sources.filter(function (s) { return s && (s.collection || s.search); });
    if (!sources.length) return Promise.resolve([]);

    return Promise.all(sources.map(function (one) {
      return fetchSource(one, one.maxPages || maxPages);
    })).then(function (lists) {
      var seen = {};
      var merged = [];
      lists.forEach(function (cards) {
        cards.forEach(function (card) {
          var handle = card.getAttribute('data-vro-handle');
          if (handle && seen[handle]) return;
          if (handle) seen[handle] = true;
          merged.push(card);
        });
      });
      return merged;
    });
  }

  /* ---------------------------------------------------------------
     Public: answers in, ranked cards out.
  --------------------------------------------------------------- */
  /* The department a chip means, however it was worded. Exact key first —
     that is the common case and cheapest — then the normalized name, then
     the alias list, then the leading word ("Technology & Gadgets" -> "tech"
     via the aliases; "Hair & Scalp care" -> "hair"). Returns null only when
     the wording matches no department at all, which is the one case worth
     telling the shopper about. */
  function categoryKeyFor(name) {
    if (!name) return null;
    if (CATEGORIES[name]) return name;

    var wanted = norm(name);
    if (!wanted) return null;

    var key;
    for (key in CATEGORIES) {
      if (!CATEGORIES.hasOwnProperty(key)) continue;
      if (norm(key) === wanted) return key;
    }
    for (key in CATEGORY_ALIASES) {
      if (!CATEGORY_ALIASES.hasOwnProperty(key)) continue;
      if (CATEGORY_ALIASES[key].indexOf(wanted) > -1) return key;
    }
    /* Last resort: the first meaningful word. "Fashion & Lifestyle picks"
       and "Fashion" are the same department to a shopper. */
    var lead = wanted.split(' ')[0];
    if (!lead) return null;
    for (key in CATEGORY_ALIASES) {
      if (!CATEGORY_ALIASES.hasOwnProperty(key)) continue;
      if (CATEGORY_ALIASES[key].indexOf(lead) > -1) return key;
    }
    return null;
  }

  function categoryFor(name) {
    var key = categoryKeyFor(name);
    return key ? CATEGORIES[key] : null;
  }

  /* The departments a page is about, for a quiz opened on it — so a quiz
     asked on the Face Care listing offers Skin and not Hair or Tech.

     `context.collection` is a collection's handle and title: its handle is
     a department's own shelf, or its name is one of a department's scope
     tags ("Face Wash", "Shampoo & Conditioners"). `context.tags` is a
     product's tags, read through the same gate the results use. Deliberately
     not the loose aliases categoryKeyFor() accepts: "Grooming & Care" is
     the parent of both hair and skin, and naming it Skin would take the
     hair questions off the page that sells hair. Empty when the page says
     nothing certain — the quiz then offers every department, as before. */
  function departmentsFor(context) {
    context = context || {};
    var out = [];
    var handle = String(context.handle || '').trim();
    var text = norm(handle + ' ~ ' + (context.title || ''));
    var tags = context.tags ? { tags: norm(context.tags) } : null;
    for (var key in CATEGORIES) {
      if (!CATEGORIES.hasOwnProperty(key)) continue;
      var cat = CATEGORIES[key];
      var hit = false;
      if (handle || context.title) {
        hit = (handle && cat.collection === handle) ||
              (cat.scopeTags && includesAny(text, cat.scopeTags));
      } else if (tags) {
        hit = !!(cat.scopeTags && cat.scopeTags.length) && inDepartment(tags, cat);
      }
      if (hit) out.push(key);
    }
    return out;
  }

  /* The questions to ask about a department, resolved the same tolerant
     way, so a renamed chip still asks the right things. */
  function problemsFor(name) {
    var written = adminOptions(name, 'q1');
    if (written.length) return written;
    var key = categoryKeyFor(name);
    return (key && PROBLEM_SETS[key]) ? PROBLEM_SETS[key].slice() : [];
  }

  function conditionsFor(name) {
    var key = categoryKeyFor(name);
    var set = (key && CONDITION_SETS[key]) || CONDITION_SETS['default'];
    var written = adminOptions(name, 'q2');
    return {
      title: adminTitle(name, 'q2') || set.title,
      options: written.length ? written : set.options.slice()
    };
  }

  /* ---------------------------------------------------------------
     The admin's own words, where it has supplied them.

     sections/quiz-content.liquid prints one JSON island per page: a
     department, its four questions and their options. Everything below
     asks it first and falls through to the built-in sets field by field,
     so a block that fills in only a title changes only that title.

     Read on first use rather than at load: the island is rendered from
     the layout and the script is loaded in the head, so there is no order
     in which "read it now" would be safe. */
  var adminContent;

  function quizContent() {
    if (adminContent) return adminContent;
    adminContent = {};
    var el = document.getElementById('vro-quiz-content');
    if (!el) return adminContent;
    try {
      var parsed = JSON.parse(el.textContent || '{}');
      if (parsed && typeof parsed === 'object') adminContent = parsed;
    } catch (e) { /* a malformed island is no island */ }

    /* The price bands the admin wrote, in the same paise the cards are
       stamped with, folded into the map every gate reads. */
    var key;
    for (key in adminContent) {
      if (!adminContent.hasOwnProperty(key)) continue;
      var q4 = adminContent[key] && adminContent[key].q4;
      bandsFrom(q4 && q4.bands).forEach(function (band) {
        BUDGETS[band.label] = [band.min, band.max];
      });
    }
    return adminContent;
  }

  /* "Label | 500 | 1000" a line, in rupees. A missing top is open-ended —
     it has to be a real number rather than Infinity, because a band the
     map cannot read is a price gate silently skipped. */
  function bandsFrom(text) {
    return lines(text).map(function (line) {
      var parts = line.split('|');
      var label = String(parts[0] || '').trim();
      if (!label) return null;
      var low = parseFloat(String(parts[1] || '').replace(/[^0-9.]/g, ''));
      var high = parseFloat(String(parts[2] || '').replace(/[^0-9.]/g, ''));
      return {
        label: label,
        min: isNaN(low) ? 0 : Math.round(low * 100),
        max: isNaN(high) ? 100000000 : Math.round(high * 100)
      };
    }).filter(Boolean);
  }

  function lines(text) {
    return String(text == null ? '' : text)
      .split(/\r?\n/)
      .map(function (line) { return line.trim(); })
      .filter(Boolean);
  }

  /* How many products this department holds, as Liquid counted them.

     Every Noted bar reads "N from M". M has to be the shelf's real size,
     not the slice this script happened to scan — a 290-product category
     read four pages deep would otherwise say "from 200" on one surface
     and "from 148" on another, about the same shelf. sections/
     quiz-content.liquid prints the true counts; where one is missing the
     caller falls back to what it counted itself. */
  var categoryTotals;

  function totalFor(name) {
    if (!categoryTotals) {
      categoryTotals = {};
      var el = document.getElementById('vro-category-totals');
      if (el) {
        try {
          var parsed = JSON.parse(el.textContent || '{}');
          if (parsed && typeof parsed === 'object') categoryTotals = parsed;
        } catch (e) { /* a malformed island is no island */ }
      }
    }
    var key = categoryKeyFor(name);
    if (key && categoryTotals[key] > 0) return categoryTotals[key];
    var wanted = norm(key || name);
    var k;
    for (k in categoryTotals) {
      if (categoryTotals.hasOwnProperty(k) && norm(k) === wanted && categoryTotals[k] > 0) {
        return categoryTotals[k];
      }
    }
    return 0;
  }

  /* The admin's entry for this department, however it was spelled there —
     "hair", "Hair Care" and "Hair & Scalp" are the same shelf. */
  function adminFor(name) {
    var all = quizContent();
    var key = categoryKeyFor(name);
    var wanted = norm(key || name);
    if (!wanted) return null;
    var k;
    for (k in all) {
      if (!all.hasOwnProperty(k)) continue;
      if (norm(k) === wanted || norm(categoryKeyFor(k) || k) === wanted) return all[k];
    }
    return null;
  }

  function adminTitle(name, layer) {
    var entry = adminFor(name);
    var title = entry && entry[layer] && entry[layer].title;
    return title ? String(title).trim() : '';
  }

  function adminOptions(name, layer) {
    var entry = adminFor(name);
    return lines(entry && entry[layer] && entry[layer].options);
  }

  /* The first question's wording for this department, in its own words. */
  function problemTitleFor(name) {
    var said = adminTitle(name, 'q1');
    if (said) return said;
    var key = categoryKeyFor(name);
    if (key && PROBLEM_TITLES[key]) return PROBLEM_TITLES[key];
    var noun = String(name || '').split(' ')[0].toLowerCase();
    return noun ? 'Pick your ' + noun + ' problem' : 'Pick your problem';
  }

  /* The money question's bands for this department. */
  function budgetsFor(name) {
    var entry = adminFor(name);
    var written = bandsFrom(entry && entry.q4 && entry.q4.bands);
    if (written.length) {
      /* Reading the bands is also what registers them, so a label the
         admin invented is a price gate and not a word. */
      quizContent();
      return written.map(function (band) { return band.label; });
    }
    var key = categoryKeyFor(name);
    return ((key && BUDGET_SETS[key]) || BUDGET_OPTIONS).slice();
  }

  function budgetTitleFor(name) {
    var said = adminTitle(name, 'q4');
    if (said) return said;
    var key = categoryKeyFor(name);
    return (key && BUDGET_TITLES[key]) || 'Tell us your budget';
  }

  /* null where a department has no such question, which is how a card
     knows to skip the step rather than draw it empty. */
  function traitsFor(name) {
    var entry = adminFor(name);
    if (entry && entry.q3 && entry.q3.skip === true) return null;
    var written = adminOptions(name, 'q3');
    var key = categoryKeyFor(name);
    var set = key && TRAIT_SETS[key];
    if (!written.length && !set) return null;
    return {
      title: adminTitle(name, 'q3') || (set && set.title) || '',
      options: written.length ? written : set.options.slice()
    };
  }

  /* The merchandising team's own picks for these answers, where they have
     sheeted the department (assets/vro-quiz-picks.js — Hair & Scalp,
     Health & Energy, Fragrance, Lifestyle, Fashion & Styling), shown in
     their order instead of the automatic ranking below. Only when at least
     one of those products loads; otherwise the automatic picks, as before. */
  function findMatches(answers, options) {
    options = options || {};
    var category = categoryFor(answers.category);
    if (!window.VroQuizPicks || !category) return autoMatches(answers, options);
    var limit = options.show || DEFAULT_SHOW;
    return window.VroQuizPicks.handlesFor(answers).then(function (picks) {
      if (!picks) return autoMatches(answers, options);
      return window.VroQuizPicks.cardsFor(picks.slice(0, limit)).then(function (cards) {
        /* A sheet is typed by hand, so it is held to the same department
           line as the automatic picks: nothing from the other shelf. */
        cards = cards.filter(function (card) { return !inOtherDepartment(cardData(card), category); });
        if (!cards.length) return autoMatches(answers, options);
        return {
          cards: cards,
          total: cards.length,
          considered: cards.length,
          shelfTotal: totalFor(answers.category) || 0,
          reason: 'ok',
          inDepartment: cards.length,
          category: category,
          curated: true
        };
      });
    }).catch(function () { return autoMatches(answers, options); });
  }

  function autoMatches(answers, options) {
    options = options || {};
    var category = categoryFor(answers.category);

    if (!category) {
      return Promise.resolve({ cards: [], reason: 'no-category', category: null });
    }

    /* The mapped shelf first, then the inventory by what the shopper
       actually said. A category whose collection is thin, mis-wired or
       empty — Technology has no products in it at all — would otherwise
       hand back nothing, however well the store is stocked. Search only
       widens what is looked at; the budget, problem and condition gates
       below still decide what survives. */
    var sources = [];
    if (category.collection) sources.push({ collection: category.collection });
    var terms = searchTerms(answers);
    if (terms) sources.push({ search: terms, maxPages: 2 });

    return fetchPool(sources, options.maxPages)
      .then(function (cards) {
        if (!cards.length) return { cards: [], reason: 'empty-shelf', category: category };

        var seen = {};
        var scored = [];
        /* How many candidates were in the shopper's department at all. */
        var inDept = 0;
        /* Distinct candidates, which is the honest denominator for the row. */
        var seenCount = 0;
        for (var i = 0; i < cards.length; i++) {
          var handle = cards[i].getAttribute('data-vro-handle');
          if (handle && seen[handle]) continue;
          if (handle) seen[handle] = true;
          seenCount++;

          var data = cardData(cards[i]);
          if (data && inDepartment(data, category)) inDept++;

          var hit = scoreCard(cards[i], answers, category);
          if (hit) { hit.order = i; scored.push(hit); }
        }

        scored.sort(function (a, b) {
          return b.score - a.score || a.order - b.order;
        });

        var limit = options.show || DEFAULT_SHOW;
        return {
          cards: scored.slice(0, limit).map(function (hit) { return hit.card; }),
          total: scored.length,
          /* How many products were actually looked at, after de-duping the
             shelf and the search. The count above the row reads "3 from
             120" — what is on screen out of what was considered — and
             saying "3 from 3" told a shopper nothing: the denominator was
             the same filtered set as the numerator. */
          considered: seenCount,
          /* The shelf's real size where Liquid could count it, so a bar
             says "from 290" and not "from the 200 I looked at". */
          shelfTotal: totalFor(answers.category) || 0,
          reason: scored.length ? 'ok' : (inDept ? 'no-match' : 'no-stock'),
          inDepartment: inDept,
          category: category
        };
      })
      .catch(function () {
        return { cards: [], reason: 'error', category: category };
      });
  }

  /* A shopper-facing sentence describing what was applied. */
  function summary(answers) {
    var parts = [];
    if (answers.problems && answers.problems.length) parts.push(answers.problems.join(', ').toLowerCase());
    if (answers.condition) parts.push(answers.condition.toLowerCase());
    if (answers.budget) parts.push(answers.budget);
    return parts.length ? 'For ' + parts.join(' · ') : '';
  }

  /* The "see everything" escape hatch keeps pointing at ordinary search,
     which is deliberately left exactly as it was for normal searches. */
  function searchTerms(answers) {
    var terms = [];
    if (answers.category) terms.push(answers.category.split(' & ')[0]);
    if (answers.problems) terms = terms.concat(answers.problems);
    return terms.filter(Boolean).join(' ').trim();
  }

  /* type=product for the filters (a search without it returns none, so the
     bar on the results page holds nothing but Sort), and vro_cat so that
     page can name the department instead of repeating the query as a
     headline. */
  function searchUrl(answers) {
    var terms = [];
    if (answers.category) terms.push(answers.category.split(' & ')[0]);
    if (answers.problems) terms = terms.concat(answers.problems);
    if (answers.condition) terms.push(answers.condition);
    var url = '/search?q=' + encodeURIComponent(terms.filter(Boolean).join(' ')) + '&type=product';
    if (answers.category) url += '&vro_cat=' + encodeURIComponent(answers.category);
    return url;
  }

  /* ---------------------------------------------------------------
     In-section facets (the size card, the skin-type card).

     Same structured data, one answer instead of four, and applied to the
     cards a section already rendered rather than to a fetched shelf.
  --------------------------------------------------------------- */
  function facetCondition(facet, label) {
    var table = FACET_VALUES[facet];
    if (!table) return norm(label);
    return table[norm(label)] || norm(label);
  }

  function facetScore(card, facet, label, typeField) {
    var data = cardData(card);
    if (!data) return 0;
    var condition = facetCondition(facet, label);
    var rule = CONDITIONS[condition];
    if (!rule) return 0;

    var declared = typeFor(data, typeField || 'skin');
    if (declared && !typeIsUniversal(declared) && includesAny(declared, rule.avoidTypes)) return -1;

    /* A product that works against the answer, said in its own words. Read
       before the boosts, so a mass gainer whose copy also says "muscle"
       still loses to "losing weight" rather than surviving on the overlap. */
    if (rule.avoid && rule.avoid.length &&
        (includesAny(data.blob, rule.avoid) || includesAny(data.benefits, rule.avoid))) return -1;

    /* Only a real signal counts as a match: the product says it is for this
       type, or its benefits address it. "All skin types" is not a signal —
       it is the absence of one, and scoring it as a match was enough to put
       every product in the rail above zero, so the filter had nothing left
       to narrow and answering the question appeared to do nothing. */
    var score = 0;
    if (declared && declared.indexOf(condition) > -1) score += 5;
    if (rule.boost.length && includesAny(data.blob, rule.boost)) score += 3;
    /* Same reasoning as the quiz: a claim in Key Benefits outranks a match
       anywhere else, because that is the field the catalogue fills
       properly. */
    if (rule.boost.length && includesAny(data.benefits, rule.boost)) score += 2;
    return score;
  }

  /* Can the picks section actually be seen at this width? It stamps the
     width below which it is hidden (see sections/vro-personalized-results
     .liquid); no stamp means it shows everywhere. Deliberately not
     getComputedStyle: the section carries `hidden` until it has picks, so
     it reads as display:none exactly when it is waiting to be filled. */
  function canShow(el) {
    var below = el.getAttribute('data-vro-picks-hidden-below');
    if (!below) return true;
    if (!window.matchMedia) return true;
    return !window.matchMedia('(max-width: ' + below + 'px)').matches;
  }

  /* ---------------------------------------------------------------
     "What does your daily nutrition include?"

     A different shape of question: not a concern or a type, but a named
     thing that is either in the product or is not. So it is answered from
     the ingredient list rather than from benefits — and from the shelf
     rather than from a keyword search, because Shopify does not index
     metafields. Searching "Calcium" finds the products that happen to say
     it in their copy and misses the ones that actually contain it; reading
     custom.ingredients finds the ones that contain it, which is the
     question the shopper asked.

     Search still comes along as a second source, for a store where the
     ingredient is in the title but not yet in the metafield.
  --------------------------------------------------------------- */
  function findByIngredient(term, options) {
    options = options || {};
    var wanted = norm(term);
    if (!wanted) return Promise.resolve({ cards: [], total: 0, reason: 'no-term' });

    var scope = norm(options.scopeTag || '');
    var sources = [];
    if (options.collection) sources.push({ collection: options.collection, maxPages: options.maxPages || 5 });
    sources.push({ search: term + (options.scopeTag ? ' ' + options.scopeTag : ''), maxPages: 1 });

    return fetchPool(sources).then(function (cards) {
      var scored = [];
      cards.forEach(function (card, i) {
        var data = cardData(card);
        if (!data) return;
        /* Right department first — an ingredient name can appear in a
           shampoo, and this row is about supplements. */
        if (scope && data.tags.indexOf(scope) === -1) return;

        var inList = data.ingredients.indexOf(wanted) > -1;
        var inRest = data.blob.indexOf(wanted) > -1;
        if (!inList && !inRest) return;

        /* Named in the ingredient list beats mentioned anywhere else. */
        scored.push({ card: card, score: inList ? 10 : 3, order: i });
      });

      scored.sort(function (a, b) { return b.score - a.score || a.order - b.order; });
      return {
        cards: scored.slice(0, options.show || 4).map(function (hit) { return hit.card; }),
        total: scored.length,
        reason: scored.length ? 'ok' : 'no-match'
      };
    }).catch(function () {
      return { cards: [], total: 0, reason: 'error' };
    });
  }

  /* ---------------------------------------------------------------
     Handing four answers to the section that shows them.

     Three surfaces ask the same four questions — the card on the home page,
     the modal, and the "What's bugging you?" banner — and all three used to
     end at /search. They now all call this instead: it saves the answers
     and tells the picks section to fill itself. It returns false when that
     section is not on the page (a product page, say) or is not shown at
     this width (it can be set to desktop only), which is each caller's cue
     to fall back to what it did before, so nothing anywhere ends up with a
     dead last step.
  --------------------------------------------------------------- */
  function deliver(answers, source, options) {
    if (!answers || !answers.category) return false;

    var picks = document.querySelector('[data-vro-picks-section]');
    if (!picks) return false;
    /* On the page is not the same as on screen. The section can be set to
       desktop only, and it is still in the DOM on a phone — so a hand-off
       that only checked for its presence closed the quiz, collapsed the
       card and posted the picks into something with display:none. The
       shopper answered four questions and got nothing back. Declining here
       is what puts each caller back on its own renderer, which is the same
       path a page without this section has always taken. */
    if (!canShow(picks)) return false;
    options = options || {};
    emit('vro:quiz:complete', {
      answers: JSON.parse(JSON.stringify(answers)),
      source: source || 'quiz',
      /* Where the shopper was standing when they finished. The picks move
         to sit directly under it, so the answer appears where the question
         was asked rather than wherever the section happens to sit in the
         template — the quiz banner is fourth on the home page and the picks
         section is eighth, which is a long way to send someone for an
         answer they just gave. */
      origin: options.origin || null,
      /* Only a section that IS the quiz folds away. The modal can be opened
         from a nav button or a collection chip, and collapsing whatever
         happened to contain that would take half the page with it. */
      collapseOrigin: options.collapse === true,
      scroll: options.scroll !== false
    });
    return true;
  }

  /* The quiz has been answered, so it stops asking. Collapsed rather than
     removed: "Retake" brings it back exactly as it was. */
  /* The question folds away: it fades while its height closes, so what
     follows rises into the space instead of jumping up by a card's height
     in one frame. Expand runs the same movement backwards. */
  function rollReduced() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function clearRoll(el) {
    el.style.height = '';
    el.style.overflow = '';
    el.style.transition = '';
    el.style.opacity = '';
  }

  function collapse(el) {
    if (!el || el.classList.contains('vro-collapsed')) return;
    var h = el.getBoundingClientRect().height;
    var animate = h > 0 && !rollReduced();
    if (animate) {
      el.style.height = h + 'px';
      el.style.overflow = 'hidden';
      void el.offsetHeight;
      el.style.transition = 'height .52s cubic-bezier(.32, .06, .2, 1), opacity .36s ease, transform .44s cubic-bezier(.32, .06, .2, 1)';
      el.style.height = '0px';
    }
    el.classList.add('vro-collapse-leaving');
    var done = function (e) {
      if (e && (e.target !== el || (animate && e.propertyName !== 'height'))) return;
      el.classList.add('vro-collapsed');
      el.classList.remove('vro-collapse-leaving');
      clearRoll(el);
      clearTimeout(el.__vroCollapseTimer);
      el.__vroCollapseTimer = null;
      el.removeEventListener('transitionend', done);
    };
    el.addEventListener('transitionend', done);
    /* Outlasts the transition — a backstop that fires first is what cuts a
       collapse off half way. */
    el.__vroCollapseTimer = setTimeout(done, 620);
  }

  function expand(el) {
    if (!el) return;
    clearTimeout(el.__vroCollapseTimer);
    el.__vroCollapseTimer = null;
    var wasHidden = el.classList.contains('vro-collapsed') || el.classList.contains('vro-collapse-leaving');
    el.classList.remove('vro-collapsed', 'vro-collapse-leaving');
    clearRoll(el);
    if (!wasHidden || rollReduced()) return;
    var h = el.getBoundingClientRect().height;
    if (!h) return;
    el.style.overflow = 'hidden';
    el.style.height = '0px';
    el.style.opacity = '0';
    void el.offsetHeight;
    el.style.transition = 'height .52s cubic-bezier(.22, .9, .3, 1), opacity .4s ease .1s';
    el.style.height = h + 'px';
    el.style.opacity = '1';
    el.__vroCollapseTimer = setTimeout(function () {
      el.__vroCollapseTimer = null;
      clearRoll(el);
    }, 640);
  }

  /* One short line naming what was applied, for the acknowledgement the
     picks lead with. */
  function noted(answers) {
    var bits = [];
    if (answers.problems && answers.problems.length) bits.push(answers.problems.join(' + ').toLowerCase());
    else if (answers.category) bits.push(answers.category.toLowerCase());

    var detail = [];
    if (answers.category) detail.push(answers.category.toLowerCase());
    if (answers.condition) detail.push(answers.condition.toLowerCase());
    if (answers.trait) detail.push(answers.trait.toLowerCase());
    if (answers.budget) detail.push(answers.budget);

    return {
      title: 'Noted — ' + (bits.join(', ') || 'your answers'),
      detail: detail.length ? 'Matched on ' + detail.join(' · ') + '.' : ''
    };
  }

  function reset() {
    emit('vro:quiz:reset', {});
  }

  /* Smooth where it is supported, instant where it is not, and silent where
     the element cannot be scrolled to at all — a scroll is never a reason
     for the picks not to appear. */
  function scrollTo(target) {
    if (!target || typeof target.scrollIntoView !== 'function') return;
    try { target.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
    catch (e) {
      try { target.scrollIntoView(); } catch (e2) { /* nothing more to try */ }
    }
  }

  /* ---------------------------------------------------------------
     Results, drawn where the caller stands.

     deliver() hands the answers to the "Picked for you" section, which is
     the better place for them — but that section can be set to desktop
     only, and on a phone it is not there to hand anything to. What used to
     happen then was a fall back to assets/quiz-results.js, which searches
     Shopify for the words the shopper picked and filters by price. No
     category, no relevance: the exact behaviour this engine exists to
     replace, still reachable from every quiz on a phone.

     So the quiz card gets the strict pipeline too. Same gates, same
     ranking, same answers — only the container differs.
     --------------------------------------------------------------- */
  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
  }

  /* What to say when there is nothing, and why. A shopper who asked about
     screen protection in a shop that stocks no electronics is owed that
     sentence, not three products that merely cost the right amount. */
  function emptyLine(result, answers) {
    var name = (result.category && result.category.label) || (answers.category || '').toLowerCase();
    if (result.reason === 'no-category') {
      return 'We could not tell which part of the shop that was. Try the full search.';
    }
    if (result.reason === 'empty-shelf' || result.reason === 'no-stock') {
      /* Not "try a wider budget": widening nothing still gives nothing.
         Every Technology collection on this store holds zero products, and
         a shopper who asked about screen protection is owed that sentence
         rather than whatever else happened to cost the same. */
      return 'We do not stock ' + name + ' yet — nothing to match against.';
    }
    if (result.reason === 'error') {
      return 'We could not load your picks just now.';
    }
    var asked = (answers.problems || []).join(' and ').toLowerCase();
    return asked
      ? 'Nothing in ' + name + ' answers ' + asked + ' in that budget. Try a wider budget, or fewer problems.'
      : 'Nothing in ' + name + ' fits that budget just now.';
  }

  /* options: { show, onRendered, emptyClass, railClass, noteClass, note }.
     `note: false` leaves out the summary line, for a caller whose own
     Noted bar already carries it. `onRendered(drew, result)` is handed the
     match result, so such a caller can write its count without asking for
     the shelf twice.
     Resolves true when it drew products, false when it drew an empty
     state — so a caller can still decide to do something else. */
  function renderInto(container, answers, options) {
    if (!container) return Promise.resolve(false);
    options = options || {};

    container.innerHTML = '';
    container.appendChild(el('p', options.noteClass || 'quiz-results-loading', 'Finding your picks…'));

    return findMatches(answers, { show: options.show || DEFAULT_SHOW })
      .then(function (result) {
        container.innerHTML = '';
        var wrap = el('div', 'quiz-results');

        /* The summary line, unless the caller is drawing a Noted bar of its
           own — that bar says the same thing in its detail row, and both
           together is the answer twice. */
        var note = options.note === false ? '' : summary(answers);
        if (note) wrap.appendChild(el('p', 'quiz-results-note', note));

        if (!result.cards.length) {
          wrap.appendChild(el('p', 'quiz-results-note quiz-results-note--soft',
            emptyLine(result, answers)));
          container.appendChild(wrap);
          if (options.onRendered) options.onRendered(false, result);
          return false;
        }

        var rail = el('div', (options.railClass || 'quiz-results-cards') + ' rail-scroll');
        result.cards.forEach(function (card) { rail.appendChild(card); });

        /* The tile that closes the row — the same one assets/quiz-results.js
           ends on, borrowed rather than rebuilt so the two engines cannot
           drift.

           Only when there is actually more to see. The phone draws these
           picks as a 3-up grid of five (see custom.css), so past five the
           tile is the sixth cell and means "and the rest"; at five or fewer
           every match is already on screen and the tile would send a shopper
           to a page holding exactly what they are looking at. A result of
           four, which is what "4 from 4" describes, ends on the fourth
           product. Skipped too when the engine that owns the tile has not
           loaded. */
        /* The team's picks end on it too: their sheet has a View all for
           every combination, and five is all it names. */
        if (options.viewAll !== false &&
            (result.cards.length > GRID_SHOW || result.curated) &&
            window.VroQuizResults && window.VroQuizResults.viewAllCard) {
          /* The team's picks go on to the rest of their own sheet for this
             problem, not to a keyword search — "Hair Itchy scalp" searched
             as words also finds hair wigs and hair colour. */
          if (result.curated && window.VroQuizPicks && window.VroQuizPicks.viewAllUrl &&
              window.VroQuizResults.viewAllTile) {
            rail.appendChild(window.VroQuizResults.viewAllTile(window.VroQuizPicks.viewAllUrl(answers), answers.category || 'matches'));
          } else {
            rail.appendChild(window.VroQuizResults.viewAllCard(answers));
          }
        }

        wrap.appendChild(rail);
        container.appendChild(wrap);

        if (window.VroWishlist && window.VroWishlist.sync) window.VroWishlist.sync();
        /* `result` as a second argument: a caller that draws its own bar
           needs the totals, and fetching them again would be a second pass
           over the same shelf. Existing callers take one argument and are
           unaffected. */
        if (options.onRendered) options.onRendered(true, result);
        return true;
      })
      .catch(function () {
        container.innerHTML = '';
        container.appendChild(el('p', 'quiz-results-note quiz-results-note--soft',
          'We could not load your picks just now.'));
        if (options.onRendered) options.onRendered(false);
        return false;
      });
  }

  window.VroPersonalization = {
    profile: profile,
    deliver: deliver,
    reset: reset,
    scrollTo: scrollTo,
    collapse: collapse,
    expand: expand,
    noted: noted,
    categories: CATEGORIES,
    categoryFor: categoryFor,
    budgets: BUDGETS,
    renderInto: renderInto,
    /* The quiz's own vocabulary, so no surface has to keep its own copy.
       Category names here are the only ones CATEGORIES can match. */
    quiz: {
      categories: Object.keys(CATEGORIES),
      problemSets: PROBLEM_SETS,
      conditionSets: CONDITION_SETS,
      budgets: BUDGET_OPTIONS.slice(),
      problemsFor: problemsFor,
      conditionsFor: conditionsFor,
      traitsFor: traitsFor,
      problemTitleFor: problemTitleFor,
      budgetsFor: budgetsFor,
      budgetTitleFor: budgetTitleFor,
      keyFor: categoryKeyFor,
      departmentsFor: departmentsFor
    },
    problemSignals: PROBLEM_SIGNALS,
    conditions: CONDITIONS,
    facetValues: FACET_VALUES,
    findMatches: findMatches,
    findByIngredient: findByIngredient,
    topicPhrases: topicPhrases,
    matchesTopic: matchesTopic,
    /* The candidate pool, for callers that rank it themselves — the rails
       rebuilding from their own collection when their question is
       answered. Cards come back fresh each call, never shared. */
    pool: fetchPool,
    scoreCard: scoreCard,
    facetScore: facetScore,
    facetCondition: facetCondition,
    summary: summary,
    searchUrl: searchUrl,
    totalFor: totalFor,
    normalize: norm
  };
  /* Scripts that load ahead of this one (the profile panel's, which sits
     earlier in the page) wait for this rather than running against a
     store that is not there yet. */
  try { document.dispatchEvent(new CustomEvent('vro:personalization:ready')); } catch (e) { /* old browser */ }

  /* Pull any server-side copy once, quietly, after the page is usable.

     Not straight away when the document has already been parsed. This file
     is deferred, and deferred scripts run with readyState already
     'interactive' — so the old "not loading, pull now" branch always ran,
     before assets/login-sheet.js (a module further down the page) had put
     window.VroSession there. No session meant no token, and the pull
     quietly did nothing on every page load. DOMContentLoaded comes after
     every deferred and module script; load is the backstop for a copy of
     this file added after that has already fired. */
  var pulledOnce = false;
  function initialPull() {
    if (pulledOnce) return;
    pulledOnce = true;
    profile.pull();
  }
  if (document.readyState === 'complete') {
    initialPull();
  } else {
    document.addEventListener('DOMContentLoaded', initialPull);
    window.addEventListener('load', initialPull);
  }

  /* Signing in happens in a sheet, without a page load. The answers this
     account gave on another device should arrive then, not on the next
     page — so the sign-in (assets/login-sheet.js) pulls too. */
  document.addEventListener('vro:session-changed', function () { profile.pull(); });
})();
